import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Button, VStack, HStack, Text, Input, Heading } from '@chakra-ui/react';
import { LuArrowLeft, LuArrowRight, LuTrophy } from 'react-icons/lu';

const GAME_WIDTH = 400;
const GAME_HEIGHT = 500;
const CAT_WIDTH = 60;
const CAT_HEIGHT = 60;
const ITEM_SIZE = 40;
const CAT_SPEED = 8;
const SPAWN_RATE = 800;
const FALL_SPEED_BASE = 2.5;
const GRASS_BASE_WEIGHT = 12;
const GRASS_MAX_WEIGHT = 80;
const MAX_GAME_DURATION = 60; // seconds until max difficulty
const ITEMS_PER_SPAWN = 3; // Number of items that spawn at once

// Snack types with points and spawn weights
const SNACK_TYPES = [
  { type: 'snack1', points: 1, color: '#FFD93D', emoji: '🍪', weight: 60 },
  { type: 'snack3', points: 3, color: '#FF6B6B', emoji: '🍕', weight: 30 },
  { type: 'snack5', points: 5, color: '#6BCB77', emoji: '🍰', weight: 10 },
];

const GRASS_TYPE = { type: 'grass', points: 0, color: '#4CAF50', emoji: '🌿' };

function Game() {
  const canvasRef = useRef(null);
  const gameLoopRef = useRef(null);
  const spawnTimerRef = useRef(null);

  const [gameState, setGameState] = useState('idle'); // idle, playing, gameover
  const [score, setScore] = useState(0);
  const [playerName, setPlayerName] = useState('');
  const [leaderboard, setLeaderboard] = useState([]);
  const [highScore, setHighScore] = useState(0);
  const [puking, setPuking] = useState(false);
  const [pukeAnimation, setPukeAnimation] = useState(false);
  const [grassCount, setGrassCount] = useState(0);
  const [scoreSaved, setScoreSaved] = useState(false);

  const catRef = useRef({ x: GAME_WIDTH / 2 - CAT_WIDTH / 2, y: GAME_HEIGHT - CAT_HEIGHT - 10 });
  const itemsRef = useRef([]);
  const keysRef = useRef({ left: false, right: false });
  const grassTimestampsRef = useRef([]);
  const gameStartTimeRef = useRef(null);
  const nomIndexRef = useRef(0);
  const sessionTokenRef = useRef(null);
  const catImageRef = useRef(null);

  // Fetch leaderboard
  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await fetch('/api/scores');
      if (res.ok) {
        const data = await res.json();
        setLeaderboard(data);
      }
    } catch (e) {
      console.log('Leaderboard not available');
    }
  }, []);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  // Load cat image
  useEffect(() => {
    const img = new Image();
    img.src = '/chmurka.png';
    img.onload = () => {
      catImageRef.current = img;
    };
  }, []);

  // Submit score
  const submitScore = async () => {
    if (!playerName.trim() || scoreSaved) return;
    try {
      const res = await fetch('/api/scores', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ player_name: playerName, score, session_token: sessionTokenRef.current }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.log('Failed to submit score:', data.detail || 'Unknown error');
        return;
      }
      setScoreSaved(true);
      fetchLeaderboard();
    } catch (e) {
      console.log('Failed to submit score');
    }
  };

  // Get random spawn item
  const getSpawnItem = (grassWeight) => {
    const totalWeight = SNACK_TYPES.reduce((sum, t) => sum + t.weight, 0);
    const rand = Math.random() * (totalWeight + grassWeight);

    if (rand < grassWeight) {
      return { ...GRASS_TYPE, x: Math.random() * (GAME_WIDTH - ITEM_SIZE), y: -ITEM_SIZE };
    }

    let accumulated = 0;
    for (const snack of SNACK_TYPES) {
      accumulated += snack.weight;
      if (rand < accumulated + grassWeight) {
        return { ...snack, x: Math.random() * (GAME_WIDTH - ITEM_SIZE), y: -ITEM_SIZE };
      }
    }
    return { ...SNACK_TYPES[0], x: Math.random() * (GAME_WIDTH - ITEM_SIZE), y: -ITEM_SIZE };
  };

  // Check collision
  const checkCollision = (item) => {
    const cat = catRef.current;
    return (
      item.x < cat.x + CAT_WIDTH &&
      item.x + ITEM_SIZE > cat.x &&
      item.y < cat.y + CAT_HEIGHT &&
      item.y + ITEM_SIZE > cat.y
    );
  };

  // Game loop
  const gameLoop = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Calculate game time and difficulty
    const gameTime = gameStartTimeRef.current ? (Date.now() - gameStartTimeRef.current) / 1000 : 0;
    const difficultyFactor = gameTime / MAX_GAME_DURATION;

    // Increase fall speed infinitely over time
    const fallSpeed = FALL_SPEED_BASE + (difficultyFactor * 2) + (score / 50);

    // Increase grass ratio over time (from 12 to 80)
    const currentGrassWeight = GRASS_BASE_WEIGHT + (difficultyFactor * (GRASS_MAX_WEIGHT - GRASS_BASE_WEIGHT));

    // Move cat
    if (keysRef.current.left && catRef.current.x > 0) {
      catRef.current.x -= CAT_SPEED;
    }
    if (keysRef.current.right && catRef.current.x < GAME_WIDTH - CAT_WIDTH) {
      catRef.current.x += CAT_SPEED;
    }

    // Update items
    itemsRef.current = itemsRef.current
      .map(item => ({ ...item, y: item.y + fallSpeed }))
      .filter(item => item.y < GAME_HEIGHT);

    // Check collisions
    const newItems = [];
    let newScore = score;
    let shouldPuke = false;

    for (const item of itemsRef.current) {
      if (checkCollision(item)) {
        if (item.type === 'grass') {
          // Track total grasses eaten
          grassTimestampsRef.current.push(Date.now());
          setGrassCount(grassTimestampsRef.current.length);
          // Check if cat should puke (3 grasses total)
          if (grassTimestampsRef.current.length >= 3 && !puking) {
            shouldPuke = true;
          }
        } else {
          newScore += item.points;
          setScore(newScore);
          if (newScore > highScore) {
            setHighScore(newScore);
          }
          // Play nom sound (cycles through nom1, nom2, nom3)
          const nomSound = new Audio(`/nom${nomIndexRef.current + 1}.wav`);
          nomSound.volume = 1.0;
          nomSound.play().catch(() => {});
          nomIndexRef.current = (nomIndexRef.current + 1) % 3;
        }
      } else {
        newItems.push(item);
      }
    }

    itemsRef.current = newItems;

    // Handle puke and game over
    if (shouldPuke && !puking) {
      setPuking(true);
      setPukeAnimation(true);
      // Stop spawning items
      if (spawnTimerRef.current) {
        clearInterval(spawnTimerRef.current);
        spawnTimerRef.current = null;
      }
      // End game after animation finishes (1500ms)
      setTimeout(() => {
        setPukeAnimation(false);
        setGameState('gameover');
        if (gameLoopRef.current) {
          cancelAnimationFrame(gameLoopRef.current);
        }
      }, 1500);
    }

    // Clear canvas
    ctx.fillStyle = '#FFF5E6';
    ctx.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // Draw game area
    ctx.fillStyle = '#8B4513';
    ctx.fillRect(0, GAME_HEIGHT - 80, GAME_WIDTH, 80);

    // Draw cat
    const cat = catRef.current;
    ctx.save();
    if (pukeAnimation) {
      // Puking animation
      ctx.translate(cat.x + CAT_WIDTH / 2, cat.y + CAT_HEIGHT / 2);
      ctx.rotate(Math.sin(Date.now() / 100) * 0.3);
      ctx.translate(-(cat.x + CAT_WIDTH / 2), -(cat.y + CAT_HEIGHT / 2));
    }

    // Draw cat image if loaded, otherwise fallback to drawing
    if (catImageRef.current) {
      ctx.drawImage(catImageRef.current, cat.x, cat.y, CAT_WIDTH, CAT_HEIGHT);
    } else {
      // Fallback: Cat body
      ctx.fillStyle = '#FF8C42';
      ctx.beginPath();
      ctx.ellipse(cat.x + CAT_WIDTH / 2, cat.y + CAT_HEIGHT / 2 + 10, 25, 20, 0, 0, Math.PI * 2);
      ctx.fill();

      // Cat head
      ctx.fillStyle = '#FF8C42';
      ctx.beginPath();
      ctx.arc(cat.x + CAT_WIDTH / 2, cat.y + 25, 20, 0, Math.PI * 2);
      ctx.fill();

      // Cat ears
      ctx.fillStyle = '#FF8C42';
      ctx.beginPath();
      ctx.moveTo(cat.x + 15, cat.y + 15);
      ctx.lineTo(cat.x + 10, cat.y);
      ctx.lineTo(cat.x + 25, cat.y + 10);
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(cat.x + 45, cat.y + 15);
      ctx.lineTo(cat.x + 50, cat.y);
      ctx.lineTo(cat.x + 35, cat.y + 10);
      ctx.fill();

      // Cat eyes
      ctx.fillStyle = '#000';
      ctx.beginPath();
      ctx.arc(cat.x + 20, cat.y + 22, 3, 0, Math.PI * 2);
      ctx.arc(cat.x + 40, cat.y + 22, 3, 0, Math.PI * 2);
      ctx.fill();

      // Cat nose
      ctx.fillStyle = '#FF6B9D';
      ctx.beginPath();
      ctx.arc(cat.x + 30, cat.y + 28, 3, 0, Math.PI * 2);
      ctx.fill();

      // Whiskers
      ctx.strokeStyle = '#333';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(cat.x + 10, cat.y + 28);
      ctx.lineTo(cat.x + 5, cat.y + 25);
      ctx.moveTo(cat.x + 10, cat.y + 30);
      ctx.lineTo(cat.x + 5, cat.y + 32);
      ctx.moveTo(cat.x + 50, cat.y + 28);
      ctx.lineTo(cat.x + 55, cat.y + 25);
      ctx.moveTo(cat.x + 50, cat.y + 30);
      ctx.lineTo(cat.x + 55, cat.y + 32);
      ctx.stroke();
    }

    // Puke animation
    if (pukeAnimation) {
      ctx.fillStyle = '#90EE90';
      ctx.font = '20px Arial';
      ctx.fillText('🤢', cat.x + 55, cat.y);
    }

    ctx.restore();

    // Draw items
    for (const item of itemsRef.current) {
      ctx.font = '30px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(item.emoji, item.x + ITEM_SIZE / 2, item.y + ITEM_SIZE - 5);
    }

    // Draw time
    ctx.fillStyle = '#333';
    ctx.font = 'bold 20px Arial';
    ctx.textAlign = 'right';
    ctx.fillText(`Time: ${Math.floor(gameTime)}s`, GAME_WIDTH - 10, 25);

    // Draw grass warning
    if (grassTimestampsRef.current.length >= 2) {
      ctx.fillStyle = '#FF4444';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`⚠️ Jeszcze ${3 - grassTimestampsRef.current.length} trawa = PRZEGRANA!`, GAME_WIDTH / 2, 55);
    }

    gameLoopRef.current = requestAnimationFrame(gameLoop);
  }, [score, highScore, puking, pukeAnimation]);

  // Start game
  const startGame = async () => {
    // Request a game session from the server
    try {
      const res = await fetch('/api/game/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        sessionTokenRef.current = data.session_token;
      } else {
        sessionTokenRef.current = null;
      }
    } catch (e) {
      sessionTokenRef.current = null;
    }

    setScore(0);
    setHighScore(0);
    setGrassCount(0);
    gameStartTimeRef.current = Date.now();
    catRef.current = { x: GAME_WIDTH / 2 - CAT_WIDTH / 2, y: GAME_HEIGHT - CAT_HEIGHT - 10 };
    itemsRef.current = [];
    grassTimestampsRef.current = [];
    keysRef.current = { left: false, right: false };
    setPuking(false);
    setPukeAnimation(false);
    setScoreSaved(false);
    setPlayerName('');
    setGameState('playing');

    // Spawn items function - defined outside to avoid closure issues
    const spawnItemsNow = () => {
      if (!gameStartTimeRef.current) return;

      const gameTime = (Date.now() - gameStartTimeRef.current) / 1000;
      const difficultyFactor = gameTime / MAX_GAME_DURATION;
      const currentGrassWeight = GRASS_BASE_WEIGHT + (difficultyFactor * (GRASS_MAX_WEIGHT - GRASS_BASE_WEIGHT));

      console.log('Spawning items, gameTime:', gameTime);

      // Spawn multiple items at once
      for (let i = 0; i < ITEMS_PER_SPAWN; i++) {
        const item = getSpawnItem(currentGrassWeight);
        console.log('Spawned item:', item);
        itemsRef.current.push(item);
      }
    };

    // Initial spawn
    spawnItemsNow();

    // Continue spawning at intervals
    spawnTimerRef.current = setInterval(spawnItemsNow, SPAWN_RATE);

    gameLoopRef.current = requestAnimationFrame(gameLoop);
  };

  // Stop game
  const stopGame = () => {
    setGameState('gameover');
    if (gameLoopRef.current) {
      cancelAnimationFrame(gameLoopRef.current);
    }
    if (spawnTimerRef.current) {
      clearInterval(spawnTimerRef.current);
    }
  };

  // Handle key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') keysRef.current.left = true;
      if (e.key === 'ArrowRight') keysRef.current.right = true;
    };

    const handleKeyUp = (e) => {
      if (e.key === 'ArrowLeft') keysRef.current.left = false;
      if (e.key === 'ArrowRight') keysRef.current.right = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
      if (spawnTimerRef.current) clearInterval(spawnTimerRef.current);
    };
  }, []);

  // Continue game loop when playing
  useEffect(() => {
    if (gameState === 'playing') {
      gameLoopRef.current = requestAnimationFrame(gameLoop);
    }
    return () => {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    };
  }, [gameState, gameLoop]);

  return (
    <VStack spacing={6} p={4} bg="semantic.muted" borderRadius="xl">
      <Heading size="lg" color="brand.600">🐱 Złap Przekąski! 🐱</Heading>

      {gameState === 'idle' && (
        <VStack spacing={4}>
          <Text color="semantic.textMuted" textAlign="center" px={4}>
            Specjalna gra o Chmurce, naszej kotce.
            Steruj kotkiem za pomocą strzałek lub przycisków ← →<br/><br/>
            Złap jak najwięcej przekąsek:<br/>
            🍪 Ciastko = 1 punkt<br/>
            🍕 Pizza = 3 punkty<br/>
            🍰 Ciasto = 5 punktów<br/><br/>
            Uważaj na trawę! 🌿<br/>
            Zjedzenie 3 traw kończy grę!
          </Text>
          <Button colorScheme="orange" size="lg" onClick={startGame}>
            START
          </Button>
          {highScore > 0 && <Text color="brand.600">Twój rekord: {highScore}</Text>}
        </VStack>
      )}

      {gameState === 'playing' && (
        <VStack spacing={4}>
          <HStack spacing={4} justify="space-between" w="100%">
            <Text fontSize="xl" fontWeight="bold">Wynik: {score}</Text>
            <Text fontSize="xl" fontWeight="bold">🌿 {grassCount}/3</Text>
          </HStack>
          <canvas
            ref={canvasRef}
            width={GAME_WIDTH}
            height={GAME_HEIGHT}
            style={{ border: '3px solid #FF8C42', borderRadius: '10px', maxWidth: '100%' }}
          />
          <HStack spacing={8} userSelect="none">
            <Button
              onMouseDown={() => keysRef.current.left = true}
              onMouseUp={() => keysRef.current.left = false}
              onMouseLeave={() => keysRef.current.left = false}
              onTouchStart={() => keysRef.current.left = true}
              onTouchEnd={() => keysRef.current.left = false}
              colorScheme="orange"
              w={{ base: "120px", md: "80px" }}
              h={{ base: "90px", md: "60px" }}
              userSelect="none"
            >
              <LuArrowLeft size={{ base: 48, md: 32 }} />
            </Button>
            <Button colorScheme="red" onClick={stopGame} userSelect="none" h={{ base: "90px", md: "60px" }}>
              KONIEC
            </Button>
            <Button
              onMouseDown={() => keysRef.current.right = true}
              onMouseUp={() => keysRef.current.right = false}
              onMouseLeave={() => keysRef.current.right = false}
              onTouchStart={() => keysRef.current.right = true}
              onTouchEnd={() => keysRef.current.right = false}
              colorScheme="orange"
              w={{ base: "120px", md: "80px" }}
              h={{ base: "90px", md: "60px" }}
              userSelect="none"
            >
              <LuArrowRight size={{ base: 48, md: 32 }} />
            </Button>
          </HStack>
        </VStack>
      )}

      {gameState === 'gameover' && (
        <VStack spacing={4}>
          <Heading size="md" color="brand.600">Koniec gry!</Heading>
          <Text fontSize="2xl" fontWeight="bold">Twój wynik: {score}</Text>

          <VStack spacing={2} w="100%">
            {!scoreSaved ? (
              <>
                <Text fontWeight="bold">Zapisz swój wynik:</Text>
                <HStack w="100%">
                  <Input
                    placeholder="Twoje imię"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                  />
                  <Button colorScheme="orange" onClick={submitScore}>
                    Zapisz
                  </Button>
                </HStack>
              </>
            ) : (
              <Text fontWeight="bold" color="green.500">Wynik zapisany!</Text>
            )}
          </VStack>

          <Button colorScheme="orange" size="lg" onClick={startGame}>
            Zagraj ponownie
          </Button>
        </VStack>
      )}

      {/* Leaderboard */}
      <VStack spacing={3} w="100%" maxW="400px">
        <HStack>
          <LuTrophy color="#FFD700" />
          <Heading size="md">Ranking</Heading>
        </HStack>
        {leaderboard.length > 0 ? (
          <VStack spacing={1} w="100%">
            {leaderboard.map((entry, idx) => (
              <HStack key={entry.id} w="100%" justify="space-between" p={2} bg={idx < 3 ? 'yellow.100' : 'transparent'} borderRadius="md">
                <HStack>
                  <Text fontWeight="bold" color={idx === 0 ? 'gold' : idx === 1 ? 'silver' : idx === 2 ? '#CD7F32' : 'inherit'}>
                    #{idx + 1}
                  </Text>
                  <Text>{entry.player_name}</Text>
                </HStack>
                <Text fontWeight="bold" color="brand.600">{entry.score}</Text>
              </HStack>
            ))}
          </VStack>
        ) : (
          <Text color="semantic.textMuted">Brak wyników</Text>
        )}
      </VStack>
    </VStack>
  );
}

export default Game;
